// Pure mounting-hole geometry for the PCB stator. No Angular / DI deps.
// NPTH (non-plated) holes on a single outer-rim bolt circle, with a copper
// keepout the DRC enforces. Mirrors pcb-stator-terminals.ts.

export type MountingScrew = 'M2' | 'M2.5' | 'M3' | 'M4';

/** NPTH clearance-fit drill diameters (mm) for each screw size. */
export const MOUNTING_SCREWS: Record<MountingScrew, number> = {
  'M2':   2.4,
  'M2.5': 2.9,
  'M3':   3.4,
  'M4':   4.5,
};

export interface MountingHole {
  x: number;
  y: number;
  holeMm: number;     // NPTH drill diameter
  keepoutMm: number;  // copper-free radius from center = holeMm/2 + traceClearance
}

export interface MountingConfig {
  count: number;          // 0 = feature off
  holeMm: number;         // resolved hole diameter (preset or manual override)
  boltRadius: number;     // bolt-circle radius (mm)
  startAngleRad: number;  // angle of first hole; -PI/2 = top
  traceClearance: number; // copper keepout gap (mm)
}

/** N holes evenly spaced on the bolt circle, starting at startAngleRad. */
export function computeMountingHoles(cfg: MountingConfig): MountingHole[] {
  const { count, holeMm, boltRadius, startAngleRad, traceClearance } = cfg;
  if (count < 1) return [];
  const keepoutMm = holeMm / 2 + traceClearance;
  const holes: MountingHole[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const angle = startAngleRad + (i * 2 * Math.PI) / count;
    holes[i] = {
      x: boltRadius * Math.cos(angle),
      y: boltRadius * Math.sin(angle),
      holeMm,
      keepoutMm,
    };
  }
  return holes;
}

/** True when every hole's keepout disk stays within [innerRadius, outerRadius]. */
export function mountingHolesFit(
  holes: MountingHole[],
  innerRadius: number,
  outerRadius: number,
): boolean {
  return holes.every(h => {
    const r = Math.hypot(h.x, h.y);
    return r - h.keepoutMm >= innerRadius && r + h.keepoutMm <= outerRadius;
  });
}
